import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import axios from "axios";
import { useMapStore } from "stores/map";

import { IProjectLayer, PROJECT_LAYER_LIST_QUERY_KEYS } from "../queries";

export interface UpdateRigFracColorByParams {
  projectId: string;
  layerId: string;
  // Full-replace semantics: both fields are always sent. Omitting overrides
  // (empty object) clears them server-side.
  colorByField: Maybe<string>;
  colorOverrides: Record<string, string>;
  /**
   * The user picked this attribute by hand, so the layer stops following the
   * focus-driven Color By. Applied inside the same optimistic transaction as
   * the cache patch: doing it at the call site instead would repaint one frame
   * against the layer's previous saved field, and would leave the layer
   * detached for the rest of the session if the save then failed.
   */
  detachFromFocus?: boolean;
}

// Shape of the cached project-layer-list query — the raw axios response whose
// `.data` is the layer array (see useProjectLayerListQuery).
interface CachedLayerListResponse {
  data: IProjectLayer[];
}

interface MutationContext {
  queryKey: [string, string];
  previous: unknown;
  didDetach: boolean;
}

async function updateRigFracColorBy(params: UpdateRigFracColorByParams) {
  const url = `${
    process.env.REACT_APP_PROJECT_SERVICE
  }/project/projectLayers/colorBy/${encodeURIComponent(
    params.projectId
  )}/${encodeURIComponent(params.layerId)}`;
  return await axios.put(url, {
    colorByField: params.colorByField,
    colorOverrides: params.colorOverrides
  });
}

/**
 * Persists a rig/frac layer's Color By selection and overrides. Optimistically
 * patches the layer-list cache in onMutate so swatches/map update within the
 * same interaction (SC-003), rolls back on error, and invalidates on settle so
 * the cache reconciles with the server.
 */
export function useUpdateRigFracColorBy() {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, UpdateRigFracColorByParams, MutationContext>(
    (params) => updateRigFracColorBy(params),
    {
      onMutate: async (params) => {
        const queryKey: [string, string] = [
          PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList,
          params.projectId
        ];
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData(queryKey);

        // Detach before the cache patch lands but inside the same transaction,
        // so the layer never renders its old saved field in between.
        const wasAttached =
          !!params.detachFromFocus &&
          !useMapStore.getState().rigFracColorByDetached[params.layerId];
        if (params.detachFromFocus) {
          useMapStore.getState().detachRigFracColorBy(params.layerId);
        }

        queryClient.setQueryData<CachedLayerListResponse>(queryKey, (old) => {
          if (!old?.data) {
            return old as CachedLayerListResponse;
          }
          return {
            ...old,
            data: old.data.map((layer) =>
              layer.layerId === params.layerId
                ? {
                    ...layer,
                    colorByField: params.colorByField,
                    colorOverrides: params.colorOverrides
                  }
                : layer
            )
          };
        });

        return { queryKey, previous, didDetach: wasAttached };
      },
      onError: (error, params, context) => {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(context.queryKey, context.previous);
        }
        // Roll the detach back too, or a failed save leaves the layer pinned to
        // its old attribute and no longer following the focus for the session.
        if (context?.didDetach) {
          useMapStore.getState().attachRigFracColorBy(params.layerId);
        }
        Sentry.withScope((scope) => {
          scope.setContext("updateRigFracColorBy", {
            projectId: params.projectId,
            layerId: params.layerId
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
        toast.error("Error updating Colour By.");
      },
      onSettled: (_data, _error, params) => {
        queryClient.invalidateQueries([
          PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList,
          params.projectId
        ]);
      }
    }
  );
}
